import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Formats date string (e.g. YYYY-MM-DD or ISO 8601) into readable localized format.
 * If already a formatted string, returns as-is.
 * @param {string} dateStr
 * @returns {string}
 */
function formatDeliveryDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return dateStr || '';
  const trimmed = dateStr.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const dateObj = new Date(year, month, day);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }
  return trimmed;
}

/**
 * @typedef {object} VariantDeliveryDateRefs
 * @property {HTMLElement} [dateText] - The span displaying the delivery date string
 */

/**
 * Custom element managing the display of variant delivery dates (ETA).
 * Listens to variant selection events across the product information section.
 * @extends Component<VariantDeliveryDateRefs>
 */
export default class VariantDeliveryDate extends Component {
  /** @type {Record<string, { has_date: boolean, date: string, raw: string }>} */
  #datesById = {};

  /** @type {Array<{ id: string, options: string[], title: string, has_date: boolean, date: string, raw: string }>} */
  #variantsList = [];

  /** @type {HTMLElement | Document | null} */
  #sectionTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadDatesMap();

    // Listen on enclosing section, product details container, or document
    this.#sectionTarget = this.closest('.shopify-section, [id*="ProductInformation-"], dialog') || document;
    this.#sectionTarget.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#sectionTarget.addEventListener('change', this.#handleOptionChange);
    this.#sectionTarget.addEventListener('click', this.#handleOptionClick);

    // Initial check on load
    this.#checkInitialVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#sectionTarget) {
      this.#sectionTarget.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
      this.#sectionTarget.removeEventListener('change', this.#handleOptionChange);
      this.#sectionTarget.removeEventListener('click', this.#handleOptionClick);
    }
  }

  #loadDatesMap() {
    const scriptEl = this.querySelector('script[data-delivery-dates]');
    if (!scriptEl?.textContent) return;

    try {
      const parsed = JSON.parse(scriptEl.textContent);
      if (parsed.byId) {
        // Structured format
        this.#mergeDates(parsed.byId);
        if (Array.isArray(parsed.variants)) {
          this.#variantsList = parsed.variants;
        }
      } else {
        // Plain id -> data format
        this.#mergeDates(parsed);
      }
    } catch (e) {
      console.warn('[variant-delivery-date] Failed to parse delivery dates JSON:', e);
    }
  }

  /**
   * Safely merges incoming dates, ensuring existing dates are NEVER erased.
   * @param {Record<string, { has_date: boolean, date: string, raw: string }>} incoming
   */
  #mergeDates(incoming) {
    if (!incoming || typeof incoming !== 'object') return;
    for (const [id, data] of Object.entries(incoming)) {
      const strId = String(id).trim();
      if (!this.#datesById[strId] || data.has_date) {
        this.#datesById[strId] = data;
      }
    }
  }

  #checkInitialVariant() {
    const initialVariantId = this.dataset.selectedVariantId || this.#resolveCurrentVariantId();
    if (initialVariantId) {
      this.updateForVariant(initialVariantId);
    }
  }

  /**
   * Resolves the currently selected variant ID from variant picker inputs or options.
   * @returns {string | null}
   */
  #resolveCurrentVariantId() {
    const picker = this.#sectionTarget?.querySelector('variant-picker') || document.querySelector('variant-picker');
    if (!picker) return null;

    // 1. Direct dataset on checked input/select
    const checkedInputs = Array.from(picker.querySelectorAll('fieldset input:checked, select option:checked'));
    for (const el of checkedInputs) {
      if (el.dataset?.variantId) {
        return String(el.dataset.variantId).trim();
      }
    }

    // 2. Match checked option values against variants list
    if (this.#variantsList.length > 0) {
      const selectedOptionValues = Array.from(picker.querySelectorAll('fieldset input:checked, select')).map((el) => {
        return el.value?.trim();
      }).filter(Boolean);

      if (selectedOptionValues.length > 0) {
        const matched = this.#variantsList.find((v) => {
          if (!v.options || v.options.length === 0) return false;
          return v.options.every((opt) => selectedOptionValues.includes(String(opt).trim()));
        });
        if (matched?.id) {
          return String(matched.id).trim();
        }
      }
    }

    // 3. Hidden input in buy buttons / product form
    const formVariantInput = this.#sectionTarget?.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
      return String(formVariantInput.value).trim();
    }

    // 4. URL query parameter
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) {
      return String(urlVariant).trim();
    }

    return null;
  }

  /**
   * Click handler for swatches / buttons inside variant picker
   * @param {Event} event
   */
  #handleOptionClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('variant-picker')) return;

    // Run on next tick after radio input's checked property updates
    setTimeout(() => {
      const variantId = this.#resolveCurrentVariantId();
      if (variantId) {
        this.updateForVariant(variantId);
      }
    }, 10);
  };

  /**
   * Change handler for radio inputs and select dropdowns inside variant picker
   * @param {Event} event
   */
  #handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('variant-picker')) return;

    let variantId = null;
    if (target instanceof HTMLInputElement && target.dataset.variantId) {
      variantId = target.dataset.variantId;
    } else if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      if (selected?.dataset?.variantId) {
        variantId = selected.dataset.variantId;
      }
    }

    if (!variantId) {
      variantId = this.#resolveCurrentVariantId();
    }

    if (variantId) {
      this.updateForVariant(variantId);
    }
  };

  /**
   * StandardEvents.productSelect event listener.
   * Handles immediate event detail and async server re-render resolution.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Fast path: immediate variantId if provided in event detail
    const immediateVariantId = event.detail?.variantId || this.#resolveCurrentVariantId();
    if (immediateVariantId) {
      this.updateForVariant(immediateVariantId);
    }

    // Await server render promise for multi-option products or combined listings
    event.promise
      .then(({ variant, detail }) => {
        if (!detail) return;

        // If product changed (e.g. combined listing)
        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
          if (detail.html) {
            const newBlock = detail.html.querySelector(`variant-delivery-date[data-block-id="${this.dataset.blockId}"]`)
              || detail.html.querySelector('variant-delivery-date');
            if (newBlock) {
              const newScript = newBlock.querySelector('script[data-delivery-dates]');
              if (newScript?.textContent) {
                try {
                  const parsed = JSON.parse(newScript.textContent);
                  if (parsed.byId) {
                    this.#mergeDates(parsed.byId);
                    if (Array.isArray(parsed.variants)) this.#variantsList = parsed.variants;
                  } else {
                    this.#mergeDates(parsed);
                  }
                } catch (e) {}
              }
            }
          }
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Cache any server-rendered date for this variant if available
        if (detail.html) {
          const newBlock = detail.html.querySelector(`variant-delivery-date[data-block-id="${this.dataset.blockId}"]`)
            || detail.html.querySelector('variant-delivery-date');
          if (newBlock) {
            const serverDateSpan = newBlock.querySelector('.variant-delivery-date__value, [ref="dateText"]');
            const serverDateText = serverDateSpan?.textContent?.trim();
            const currentId = String(variant?.id || detail.resource?.id || this.dataset.selectedVariantId || '').trim();
            if (serverDateText && currentId) {
              this.#datesById[currentId] = {
                has_date: true,
                date: serverDateText,
                raw: serverDateText,
              };
            }
          }
        }

        const resolvedVariantId = variant?.id || detail.resource?.id || this.#resolveCurrentVariantId();
        if (resolvedVariantId) {
          this.updateForVariant(resolvedVariantId);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[variant-delivery-date] Promise rejected:', error);
        }
      });
  };

  /**
   * Updates display state and date text for the specified variant ID.
   * @param {string | number} variantId
   */
  updateForVariant(variantId) {
    if (!variantId) return;
    const strId = String(variantId).trim();
    this.dataset.selectedVariantId = strId;

    const variantData = this.#datesById[strId];
    const valueEl = this.querySelector('.variant-delivery-date__value') || this.querySelector('[ref="dateText"]');

    if (variantData && variantData.has_date && (variantData.date || variantData.raw)) {
      const displayDate = formatDeliveryDate(variantData.date || variantData.raw);
      if (valueEl) {
        valueEl.textContent = displayDate;
      }
      this.classList.remove('is-hidden');
      this.style.removeProperty('display');
      this.style.display = 'block';
      this.setAttribute('aria-hidden', 'false');
    } else {
      // In design mode preview, keep visible if marked
      if (this.dataset.designPreview === 'true') {
        return;
      }
      this.classList.add('is-hidden');
      this.style.display = 'none';
      this.setAttribute('aria-hidden', 'true');
    }
  }
}

if (!customElements.get('variant-delivery-date')) {
  customElements.define('variant-delivery-date', VariantDeliveryDate);
}
